import { useCallback, useEffect, useRef, useState } from 'react'
import { setCookie, getCookie, Cookies } from '@chegg-wt/core/utils/cookies'
import { isWindowDefined } from '@chegg/utils'

export enum SprigExitIntentKey {
  EbWritingAssistantSurveyKey = 'easybib-exit-intent',
  EbPostCardInteractionSurveyKey = 'eb-post-card-interaction',
}

const useSprigTriggerExitIntent = () => {
  const [pageExitIntentStatus, setPageExitIntentStatus] = useState(false)
  const [paperId, setPaperId] = useState('')
  const sprigKeyRef = useRef('')
  const exitIntentTimeout = useRef(false)

  const onExitIntent = () => {
    if (isWindowDefined && window.Sprig) window.Sprig('track', sprigKeyRef.current)
  }

  // // Adding timeout to wait 60sec before sending survey to user.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      exitIntentTimeout.current = true
    }, 30000)

    // Cleanup on unmount
    return () => clearTimeout(timeoutId)
  }, [])

  const initiateSurveyOnPageExitIntent = useCallback(
    (sprigKey: SprigExitIntentKey, paperId?: string) => {
      sprigKeyRef.current = sprigKey
      setPageExitIntentStatus(true)
      setPaperId(paperId)
    },
    []
  )

  // SURVEY-2: after user interacts with any of grammar/clarity cards with any of update/reject/flag events.
  const initiateSurveyOnCardInteraction = useCallback((sprigKey: SprigExitIntentKey) => {
    sprigKeyRef.current = sprigKey

    // Since the same user can't receive their next study till 180 days from Sprig. We don't need to add any validation to stop re-rendering this event.
    if (
      !getCookie(Cookies.waFeebdackSurveyStatus) &&
      sprigKey === SprigExitIntentKey.EbPostCardInteractionSurveyKey
    ) {
      onExitIntent()

      // Setting cookie with expiry of 180 days, so that SURVEY-1 does not get triggered SURVEY-2 is completed.
      const expiryDate = new Date(new Date().getTime() + 180 * 24 * 60 * 60 * 1000)
      setCookie(Cookies.waFeebdackSurveyStatus, 'true', { expires: expiryDate })
    }
  }, [])

  // SURVEY-1: If user does not has paperId and user intents to exit page.
  useEffect(() => {
    if (!isWindowDefined) return

    // Case: switching Tabs or reopen after minimize.. etc
    const handleVisibilityChange = () => {
      if (exitIntentTimeout.current && document.visibilityState === 'hidden') {
        onExitIntent()
      }
    }

    // Case: Closing window or page reloading
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (exitIntentTimeout.current) {
        onExitIntent()
        event.preventDefault()
      }
    }

    // Case: when mouse moves top of viewport
    const handleMouseMove = (event: MouseEvent) => {
      if (exitIntentTimeout.current && event.clientY <= 5) {
        onExitIntent()
      }
    }

    const cleanup = () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('mousemove', handleMouseMove)
    }

    if (pageExitIntentStatus && !getCookie(Cookies.waFeebdackSurveyStatus) && !paperId) {
      document.addEventListener('visibilitychange', handleVisibilityChange, {
        once: true,
      })
      window.addEventListener('beforeunload', handleBeforeUnload, { once: true })
      window.addEventListener('mousemove', handleMouseMove)
    }
    if (paperId) cleanup()

    return () => cleanup()
  }, [pageExitIntentStatus, paperId])

  return { initiateSurveyOnPageExitIntent, initiateSurveyOnCardInteraction }
}

declare global {
  interface Window {
    Sprig: any
  }
}

export default useSprigTriggerExitIntent
